import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, zip } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { saveAs } from 'file-saver';
import { environment } from '../../../environments/environment';

export interface Vehicle {
  empresa: any;
  tipoServicio: any;
  estadoFechas: {};
  id: any;
  placa: string;
  interno: number;
  afiliacion: Date;
  estado: number;
  marca: string;
  clase: number;
  modelo: number;
  relacion: number;
  Idpropietario: string;
  sucursal: number;
  nitEmpresa: number;
  matricula: string;
  organismo: string;
  fechalict: string;
  linea: string;
  cilindraje: number;
  motor: string;
  titular: string;
  servicio: number;
  capacidad: number;
  color: string;
  serie: string;
  chasis: string;
  observaciones: string;
  carroceria: string;
  combustible: string;
  convenio: string;
  registro: string;
  password: string;
}

export interface TarjetaOperacion {
  placa: string;
  numero_tarjeta: string;
  fecha_expedicion: string;
  fecha_vencimiento: string;
  usuario_creacion: string;
}

export interface Soat {
  placa: string; // Debe coincidir con un vehículo existente
  id_soat:string;// Asegura que solo sean números
  fecha_soat:string;
  vencimiento_soat: string;
  aseguradora: string;
  valor:string;
  tarifa: string;
}

export interface Cda {
  placa: string; // Debe coincidir con un vehículo existente
  idcda:string;// Asegura que solo sean números
  centrocda:string;
  fechacda: string;
  vcda: string;
}

export interface Extintor {
  placa: string; // Debe coincidir con un vehículo existente
  idextintor:string;// Asegura que solo sean números
  frecarga:string;
  vrecarga: string;
}

export interface Bimestral {
  placa: string; // Debe coincidir con un vehículo existente
  centro_diagnostico:string;// Asegura que solo sean números
  revision_aprobada:string;
  ultima_revision: string;
  proxima_revision: string;
}

export interface Contractual {
  placa: string; // Debe coincidir con un vehículo existente
  aseguradora:string; // Asegura que solo sean números
  intermediaria:string;
  fecha_expedicion: string;
  fecha_vencimiento: string;
  numero: string;
}

export interface Extracontractual {
  placa: string; // Debe coincidir con un vehículo existente
  aseguradora:string; // Asegura que solo sean números
  intermediaria:string;
  fecha_expedicion: string;
  fecha_vencimiento: string;
  numero: string;
}

export interface Todoriesgo {
  placa: string; // Debe coincidir con un vehículo existente
  aseguradora:string; // Asegura que solo sean números
  fecha_expedicion: string;
  fecha_vencimiento: string;
  numero: string;
}


export interface StatusCounts {
  sinFecha: number;
  vencidos: number;
  porVencer: number;
  vigentes: number;
}

export interface StatusCountsCollection {
  tarjetasOperaciones: StatusCounts;
  historialSoat: StatusCounts;
  historialCda: StatusCounts;
  historialBimestral: StatusCounts;
  historialContractual: StatusCounts;
  historialExtraContractual: StatusCounts;
}

export interface GetActiveVehiclesResponse {
  length: number;
  vehicles: Vehicle[];
  statusCounts: StatusCountsCollection;
}

export interface ConteoVehiculoClase {
  claseId: number;
  tipo: string;
  cantidad: number;
}



@Injectable({
  providedIn: 'root'
})
export class VehiclesService {
  private apiUrl =  `${environment.apiUrl}/api/vehicle`; 
  //private apiUrl = 'http://localhost:3000/api/vehicle';
  private apiUrl2 = `${this.apiUrl}/estado/relacion`;
  private apiUrl3 = `${this.apiUrl}/estado/relacion/convenios`;

  constructor(private http: HttpClient) {}

  // Crear vehículo
  createVehicle(vehicle: Vehicle, tarjeta: TarjetaOperacion, soat: Soat, cda: Cda, extintor: Extintor, revisionbimestral: Bimestral, contractual: Contractual, extracontractual: Extracontractual, todoriesgo: Todoriesgo): Observable<any> {
    const payload = { vehicle, tarjeta, soat, cda, extintor, revisionbimestral, contractual, extracontractual, todoriesgo };
    return this.http.post<any>(this.apiUrl, payload, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  getVehiclesEstadoRelacion(): Observable<{ vehicles: Vehicle[], estadoFechas: any[] }> {
    return this.http.get<{ vehicles: Vehicle[], estadoFechas: any[] }>(this.apiUrl2, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

    getAll(): Observable<{ vehicles: Vehicle[] }> {
    return this.http.get<{ vehicles: Vehicle[] }>(`${this.apiUrl}/all`, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  getVehiclesEstadoRelacionTerceros(): Observable<{ vehicles: Vehicle[], estadoFechas: any[] }> {
    return this.http.get<{ vehicles: Vehicle[], estadoFechas: any[] }>(this.apiUrl3, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  getAllVehicles(id: number): Observable<Vehicle> {
    return this.http.get<Vehicle>(`${this.apiUrl}/${id}`, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }
  

  updateVehicle(placa: string, vehicle: Vehicle): Observable<any> {
    const url = `${this.apiUrl}/${placa}`;
    return this.http.put<any>(url, vehicle, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  inactivateVehicle(placa: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/${placa}/inactivate`, {}, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  getVehicleByPlaca(placa: string): Observable<Vehicle> {
    return this.http.get<Vehicle>(`${this.apiUrl}/${placa}`, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  getInactiveVehicle(): Observable<{ vehicles: Vehicle[], estadoFechas: any[] }> {
    return this.http.get<{ vehicles: Vehicle[], estadoFechas: any[] }>(`${this.apiUrl}/inactivos`, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  activateVehicle(placa: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/activar/${placa}`, {}, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  getAllActiveVehiclesWithStatusCounts(): Observable<GetActiveVehiclesResponse> {
    return this.http.get<GetActiveVehiclesResponse>(`${this.apiUrl}/list`, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  getVehiclesByStateAndRelationParticular(): Observable<any> {
    return this.http.get(`${this.apiUrl}/estado/relacion/particular`, { withCredentials: true });
  }

  getVehiclesGeneral(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/general`, { withCredentials: true });
  }

  getVehiclesPesv(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/general`, { withCredentials: true });
  }

  getVehiclesDiasRestantes(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/estado/dias`, { withCredentials: true });
  }

  getVehiclePublicoByPlaca(placa: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/estado/${placa}`, { withCredentials: true });
  }

  getPlacasUnicas(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/placas`, { withCredentials: true });
  }

  getPlacasOrdenes(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/orden/mantenimiento`, { withCredentials: true });
  }

  getPlacasAll(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/all/combustible`, { withCredentials: true });
  }

  descargarPorEstado(estado: string): void {
    const url = `${this.apiUrl}/descargar?estado=${estado}`;
    this.http.get(url, { withCredentials: true, responseType: 'blob' as 'json' }).subscribe(
      (blob: any) => {
        const file = new Blob([blob], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(file, `documentos_${estado}.xlsx`);
      },
      (error) => {
        console.error('Error al descargar Excel:', error);
      }
    );
  }

  getFechaMaximaPorPlaca(placa: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/estado/dias/${placa}`, { withCredentials: true });
  }

  getPlacas(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/placas/`, { withCredentials: true });
  }

  getVehiculosByUser(userId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/placas/user/${userId}`, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  asignarPlacaUsuario(usuarioId: number, placas: string[]): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/asignaciones`, { usuarioId, placas }, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

getContarVehiculos(): Observable<{ activos: number, inactivos: number, total: number }> {
  return this.http
    .get<{ activos: number, inactivos: number, total: number }>(
      `${this.apiUrl}/conteo`, 
      { withCredentials: true }
    )
    .pipe(catchError(this.manejarError)); // 👈 igual que en propietarios
}

getContarVehiculosPorClase() {
  return this.http
    .get<ConteoVehiculoClase[]>(`${this.apiUrl}/clase`, { withCredentials: true })
    .pipe(
      catchError(this.manejarError)
    );
}

getRuntVehicleByPlate(params: { documentType: string, documentNumber: string, plate: string }): Observable<any> {
  const queryParams = new URLSearchParams();
  queryParams.append('documentType', params.documentType);
  queryParams.append('documentNumber', params.documentNumber);
  queryParams.append('plate', params.plate);
  
  const runtUrl = `${environment.apiUrl}/api/verifik/runt/vehicle-by-plate?${queryParams.toString()}`;
  return this.http.get<any>(runtUrl, { withCredentials: true }).pipe(
    catchError(this.handleError)
  );
}

private manejarError(error: any) {
  console.error("Error en VehiculoService:", error);
  return throwError(() => error.error || "Error en la solicitud de vehículos");
}

  private handleError(error: any) {
    let message = 'Error en la solicitud.';
    if (error.status) {
      switch (error.status) {
        case 400:
          message = error.error?.message || 'Solicitud incorrecta.';
          break;
        case 401:
          message = 'No autorizado. Verifica tu sesión.';
          break;
        case 403:
          message = 'Acceso prohibido. No tienes permisos suficientes.';
          break;
        case 404:
          message = 'Recurso no encontrado.';
          break;
        case 500:
          if (error.error?.error?.name === "SequelizeUniqueConstraintError") {
            message = "La placa ya está registrada.";
          } else {
            message = 'Error interno del servidor.';
          }
          break;
        default:
          message = `Error inesperado: ${error.statusText || error.message}`;
      }
    }
    return throwError(() => new Error(message));
  }

  getPlacasBySiniestro(placa: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/siniestro/${placa}`, { withCredentials: true });
  }

  getCantidadVehiculosPlan(planData: any): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}/cantidad-vehiculos-plan`, planData, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

   getVerificarPlaca(placa: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/verificar/${placa}`, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }
}


